/**
 * Rango de fechas del filtro de periodo.
 *
 * Viaja dentro de `Record<string, Array<string>>` —la firma cerrada de
 * `<app-filter-bar>`— como un único valor `'YYYY-MM-DD|YYYY-MM-DD'`. Así el
 * rango es una pastilla más y no obliga a abrir la API de los filtros.
 */
export interface IDateRange {
  desde: string;
  hasta: string;
}

/** Separador de los dos extremos dentro del valor serializado. */
export const RANGE_SEPARATOR = '|';

const ISO = /^\d{4}-\d{2}-\d{2}$/;

const MESES_LARGOS: ReadonlyArray<string> = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

/* -------------------------------------------------------------------------- */
/*                              ANCHOR fechas                                 */
/* -------------------------------------------------------------------------- */

/**
 * `new Date('2026-07-01')` se interpreta en UTC y en México devuelve el 30 de
 * junio a las 18:00: el calendario pintaría el mes anterior. Se construye
 * siempre por componentes, que sí son locales.
 */
export const parseISO = (value: string): Date | null => {
  if (!ISO.test(value)) {
    return null;
  }

  const [anio, mes, dia] = value.split('-').map(Number);
  const fecha = new Date(anio, mes - 1, dia);

  // Rechaza un 31 de febrero, que el constructor aceptaría desbordando al mes
  // siguiente en vez de fallar.
  return fecha.getMonth() === mes - 1 && fecha.getDate() === dia ? fecha : null;
};

export const toISO = (fecha: Date): string => {
  const mes = `${fecha.getMonth() + 1}`.padStart(2, '0');
  const dia = `${fecha.getDate()}`.padStart(2, '0');

  return `${fecha.getFullYear()}-${mes}-${dia}`;
};

/** Clave de mes `YYYY-MM`: la granularidad real de los datos del tablero. */
export const monthKey = (fecha: Date): string =>
  `${fecha.getFullYear()}-${`${fecha.getMonth() + 1}`.padStart(2, '0')}`;

/** Días transcurridos entre dos fechas, ambas incluidas. */
export const daysBetween = (desde: Date, hasta: Date): number =>
  Math.round((hasta.getTime() - desde.getTime()) / 86_400_000) + 1;

export const addDays = (fecha: Date, dias: number): Date =>
  new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate() + dias);

export const addMonths = (fecha: Date, meses: number): Date =>
  new Date(fecha.getFullYear(), fecha.getMonth() + meses, 1);

export const startOfMonth = (fecha: Date): Date =>
  new Date(fecha.getFullYear(), fecha.getMonth(), 1);

/** Día 0 del mes siguiente = último día de este mes, sin tablas de 28/30/31. */
export const endOfMonth = (fecha: Date): Date =>
  new Date(fecha.getFullYear(), fecha.getMonth() + 1, 0);

export const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const clampDate = (fecha: Date, min: Date | null, max: Date | null): Date => {
  if (min && fecha < min) {
    return min;
  }

  return max && fecha > max ? max : fecha;
};

/* -------------------------------------------------------------------------- */
/*                              ANCHOR rango                                  */
/* -------------------------------------------------------------------------- */

/** Lee el valor serializado. Devuelve null si está vacío o mal formado. */
export const parseRange = (value: string | undefined | null): IDateRange | null => {
  if (!value) {
    return null;
  }

  const [desde, hasta] = value.split(RANGE_SEPARATOR);

  if (!parseISO(desde) || !parseISO(hasta)) {
    return null;
  }

  return desde <= hasta ? { desde, hasta } : { desde: hasta, hasta: desde };
};

export const serializeRange = (desde: Date, hasta: Date): string =>
  `${toISO(desde)}${RANGE_SEPARATOR}${toISO(hasta)}`;

/** `01/07/2026 – 31/07/2026`, que es como se lee una fecha en México. */
export const formatRangeLabel = (value: string): string => {
  const rango = parseRange(value);

  if (!rango) {
    return '';
  }

  return `${formatDayLabel(rango.desde)} – ${formatDayLabel(rango.hasta)}`;
};

export const formatDayLabel = (iso: string): string => {
  const fecha = parseISO(iso);

  return fecha
    ? `${`${fecha.getDate()}`.padStart(2, '0')}/${`${fecha.getMonth() + 1}`.padStart(2, '0')}/${fecha.getFullYear()}`
    : '';
};

/** «julio de 2026» — encabezado del calendario. */
export const formatMonthLabel = (fecha: Date): string =>
  `${MESES_LARGOS[fecha.getMonth()]} de ${fecha.getFullYear()}`;

/**
 * Meses que toca el rango, como claves `YYYY-MM`.
 *
 * Los datos del tablero son agregados mensuales: un rango del 10 de mayo al 3
 * de junio cubre mayo y junio enteros, porque no hay grano más fino que el mes
 * al que recortar.
 */
export const monthsInRange = (rango: IDateRange): Array<string> => {
  const desde = parseISO(rango.desde);
  const hasta = parseISO(rango.hasta);

  if (!desde || !hasta) {
    return [];
  }

  const claves: Array<string> = [];
  let cursor = startOfMonth(desde);
  const tope = startOfMonth(hasta);

  while (cursor <= tope) {
    claves.push(monthKey(cursor));
    cursor = addMonths(cursor, 1);
  }

  return claves;
};
